import { Alert, Button, CircularProgress, Link, TextField } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { Control, Controller, FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { IdentifierConfigItem } from "./networksAPI";
import { identifierValueError, isDigitsOnlyRegex } from "./identifierUtils";

type IdentifierFieldsProps = {
  items: IdentifierConfigItem[];
  // Each consuming form has its own input type; fields bind to `identifiers.<key>`.
  control: Control<any>;
  errors?: FieldErrors<any>;
  disabled?: boolean;
  // Skip the required/format checks (e.g. while saving only the email).
  skipValidation?: boolean;
  gridProps?: { xs?: number; sm?: number; md?: number };
};

/**
 * SE-3118: one text field per configured identifier, in config order, so a new
 * identifier type shows up without code changes. Values bind to
 * `identifiers.<key>` as text.
 */
export const IdentifierFields = ({
  items,
  control,
  errors,
  disabled,
  skipValidation,
  gridProps = { xs: 12, sm: 6, md: 4 }
}: IdentifierFieldsProps) => {
  const { t } = useTranslation();
  return (
    <>
      {items.map((item) => {
        const fieldError = (errors as any)?.identifiers?.[item.key];
        const hint = item.rationaleLabel ? t(item.rationaleLabel) : undefined;
        return (
          <Grid key={item.key} {...gridProps}>
            <Controller
              control={control}
              name={`identifiers.${item.key}`}
              rules={{
                validate: (raw: unknown) =>
                  skipValidation || (identifierValueError(item, raw) ?? true)
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <TextField
                  size="small"
                  onChange={onChange}
                  onBlur={onBlur}
                  label={t(item.displayName)}
                  value={value ?? ""}
                  sx={{ width: "100%" }}
                  required={item.required}
                  disabled={disabled}
                  inputProps={
                    isDigitsOnlyRegex(item.validationRegex)
                      ? { inputMode: "numeric" }
                      : undefined
                  }
                  error={!!fieldError?.message}
                  helperText={
                    fieldError?.message ? (
                      t(fieldError.message)
                    ) : item.infoUrl ? (
                      <Link href={item.infoUrl} target="_blank" rel="noopener">
                        {hint ?? t("More info")}
                      </Link>
                    ) : (
                      hint
                    )
                  }
                />
              )}
            />
          </Grid>
        );
      })}
    </>
  );
};

/** Shown in place of identifier fields while their config loads, or when it fails. */
export const IdentifierConfigStatus = ({
  status,
  onRetry
}: {
  status: "loading" | "failed";
  onRetry: () => void;
}) => {
  const { t } = useTranslation();
  if (status === "loading") {
    return (
      <Grid xs={12}>
        <CircularProgress size={20} aria-label={t("Loading identifier fields")} />
      </Grid>
    );
  }
  return (
    <Grid xs={12}>
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={onRetry}>
            {t("Try again")}
          </Button>
        }
      >
        {t("Couldn't load the identifier fields (e.g. NPI). Try again before saving.")}
      </Alert>
    </Grid>
  );
};
